import { useState, useEffect, useCallback } from 'preact/hooks';
import Modal from './shared/Modal.jsx';
import Button from './shared/Button.jsx';
import { formatEuros, formatDate, inputToCents } from '../lib/format.js';

// Losse posten van één debiteur: wat er eenmalig bij komt en niet uit een
// abonnement volgt. De implementatiefee is het geval waarvoor dit bestaat.
//
// Een post blijft openstaan tot een facturatierun hem meeneemt. Daarna is hij
// verbruikt en niet meer weg te halen: het bedrag staat dan op een factuur.
export default function PendingChargesModal({ isOpen, debtor, onClose, onChanged }) {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ description: '', quantity: '1', amount: '' });

  const refresh = useCallback(async () => {
    if (!debtor) return;
    setLoading(true);
    setFailed(false);
    try {
      const res = await fetch(`/api/pending-charges?debtorId=${debtor.id}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('load_failed');
      setCharges((await res.json()).charges || []);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [debtor]);

  useEffect(() => { if (isOpen) refresh(); }, [isOpen, refresh]);

  async function add(e) {
    e.preventDefault();
    setError(null);
    // De gebruiker typt euro's, de database bewaart centen.
    const cents = inputToCents(form.amount);
    if (cents == null || cents < 0) {
      setError('Vul een geldig bedrag in.');
      return;
    }
    const res = await fetch('/api/pending-charges', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        debtor_id: debtor.id,
        description: form.description.trim(),
        quantity: Number(form.quantity) || 1,
        unit_amount_cents: cents
      })
    });
    if (!res.ok) {
      setError('Toevoegen is niet gelukt. Controleer de omschrijving en het bedrag.');
      return;
    }
    setForm({ description: '', quantity: '1', amount: '' });
    await refresh();
    onChanged?.();
  }

  async function remove(charge) {
    setError(null);
    if (!confirm(`"${charge.description}" verwijderen?`)) return;
    const res = await fetch(`/api/pending-charges/${charge.id}`, {
      method: 'DELETE', credentials: 'same-origin'
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(
        d.error === 'charge_invoiced'
          ? 'Deze post staat al op een factuur en blijft staan.'
          : 'Verwijderen is niet gelukt.'
      );
      return;
    }
    await refresh();
    onChanged?.();
  }

  const open = charges.filter(c => c.invoiceId == null);
  const verbruikt = charges.filter(c => c.invoiceId != null);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Losse posten van ${debtor?.name || ''}`}>
      {error && <div class="notice notice-error">{error}</div>}

      {loading ? (
        <div class="empty-state muted">Laden…</div>
      ) : failed ? (
        <div class="empty-state muted">Posten konden niet worden geladen.</div>
      ) : (
        <>
          <h4>Openstaand</h4>
          {open.length === 0 ? (
            <p class="muted">Niets openstaand. De volgende factuur bevat alleen abonnementsregels.</p>
          ) : (
            <table class="data-table">
              <thead>
                <tr><th>Omschrijving</th><th>Aantal</th><th>Per stuk</th><th>Totaal</th><th /></tr>
              </thead>
              <tbody>
                {open.map(c => (
                  <tr key={c.id}>
                    <td>{c.description}</td>
                    <td class="muted">{c.quantity}</td>
                    <td class="muted">{formatEuros(c.unitAmountCents)}</td>
                    <td>{formatEuros(c.amountCents)}</td>
                    <td class="row-actions">
                      <Button variant="danger" size="sm" onClick={() => remove(c)}>Verwijderen</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {verbruikt.length > 0 && (
            <>
              <h4>Al gefactureerd</h4>
              <table class="data-table">
                <thead>
                  <tr><th>Omschrijving</th><th>Totaal</th><th>Factuur</th><th>Op</th></tr>
                </thead>
                <tbody>
                  {verbruikt.map(c => (
                    <tr key={c.id}>
                      <td class="muted">{c.description}</td>
                      <td class="muted">{formatEuros(c.amountCents)}</td>
                      <td class="muted">{c.invoiceNumber}</td>
                      <td class="muted">{formatDate(c.invoicedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}

      <form onSubmit={add} class="inline-form">
        <label>
          <span>Omschrijving</span>
          <input
            value={form.description}
            onInput={e => setForm({ ...form, description: e.currentTarget.value })}
            maxLength={200}
            placeholder="bv. Eenmalige implementatie"
            required
          />
        </label>
        <label>
          <span>Aantal</span>
          <input
            type="number" min="1" max="9999"
            value={form.quantity}
            onInput={e => setForm({ ...form, quantity: e.currentTarget.value })}
            required
          />
        </label>
        <label>
          <span>Prijs per stuk (euro, excl. btw)</span>
          <input
            value={form.amount}
            onInput={e => setForm({ ...form, amount: e.currentTarget.value })}
            placeholder="500,00"
            required
          />
        </label>
        <Button type="submit" variant="primary">Toevoegen</Button>
      </form>
    </Modal>
  );
}
